import React, { useCallback, useMemo, useState } from 'react';
import PageLoader from '../../../components/PageLoader/PageLoader';
import useServiceContextHook from '../../../_hooks/service.context.hook';
import { useSportsbookHistoryCancel } from '../command/useSportsbookHistoryCancel.command.js';
import { useSportsbookHistoryDetail } from '../detail/useSportsbookHistoryDetail.detail.js';
import {
  canHideSportsbookHistoryRow,
  getSportsbookHistoryHideId,
  readHiddenSportsbookHistoryIds,
  writeHiddenSportsbookHistoryIds,
} from './sportsbookHistoryHiddenRows.display.js';
import { adaptEstablishedSportsBetHistory } from '../adapters/establishedSportsBetHistory.adapter.js';
import { useSportsbookHistoryQuery } from '../query/useSportsbookHistoryQuery.query.js';

const STATUS_FILTERS = [
  { value: '', label: '전체' },
  { value: 'CONFIRMED', label: '확정' },
  { value: 'WON', label: '당첨' },
  { value: 'LOST', label: '낙첨' },
];

const UnifiedSportsbookHistory = ({
  dateRange,
  customStartDate,
  customEndDate,
  CardComponent,
  CursorPaginationComponent,
}) => {
  const { services: { SportsbookHistoryService } } = useServiceContextHook();
  const [status, setStatus] = useState('');
  const [hiddenHistoryIds, setHiddenHistoryIds] = useState(readHiddenSportsbookHistoryIds);

  const query = useSportsbookHistoryQuery({
    service: SportsbookHistoryService,
    dateRange,
    customStartDate,
    customEndDate,
    status,
    category: '',
    hiddenHistoryIds,
  });
  const visibleRows = useMemo(() => (
    query.rows.filter((row) => !hiddenHistoryIds.has(row.history_id))
  ), [hiddenHistoryIds, query.rows]);
  const detail = useSportsbookHistoryDetail({
    service: SportsbookHistoryService,
    rows: visibleRows,
    onVersionMismatch: query.refresh,
  });
  const refreshDetail = detail.refresh;
  const refreshList = query.refresh;

  const refreshAfterCommand = useCallback((row) => {
    refreshDetail(row);
    refreshList();
  }, [refreshDetail, refreshList]);
  const { cancel, cancelingHistoryId } = useSportsbookHistoryCancel({
    service: SportsbookHistoryService,
    onRefresh: refreshAfterCommand,
  });

  const handleCancel = useCallback(async (row) => {
    if (row.capabilities?.can_cancel !== true) return;
    if (!window.confirm('베팅을 취소하시겠습니까?')) return;
    const result = await cancel(row);
    if (result?.error) window.alert(result.error);
  }, [cancel]);

  const handleHide = useCallback((row) => {
    if (!canHideSportsbookHistoryRow(row)) return;
    const historyId = getSportsbookHistoryHideId(row);
    if (!historyId || !window.confirm('선택한 베팅내역을 화면에서 삭제하시겠습니까?')) return;
    setHiddenHistoryIds((current) => {
      const next = new Set(current);
      next.add(historyId);
      writeHiddenSportsbookHistoryIds(next);
      return next;
    });
  }, []);
  if (!CardComponent) return null;

  return (
    <div className="sp_bh_wrap">
      <div className="sp_bh_filters">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            className={`sp_bh_filter_btn${status === filter.value ? ' active' : ''}`}
            onClick={() => setStatus(filter.value)}
          >
            {filter.label}
          </button>
        ))}
      </div>
      <div className="sp_bh_meta">
        현재 {visibleRows.length.toLocaleString()}건 · 페이지당 {query.pageLimit}건 기준
      </div>

      {query.error && <div className="sp_bh_empty">{query.error}</div>}
      {query.loading && visibleRows.length === 0 ? (
        <div className="sp_bh_loading"><PageLoader /></div>
      ) : visibleRows.length === 0 ? (
        query.error ? null : <div className="sp_bh_empty">스포츠 베팅 내역이 없습니다</div>
      ) : (
        <div className="sp_bh_list">
          {visibleRows.map((row) => {
            const detailState = detail.getState(row);
            return (
              <CardComponent
                key={row.history_id}
                bet={adaptEstablishedSportsBetHistory(
                  row,
                  detailState.status === 'ready' ? detailState.detail : null,
                )}
                detailState={detailState}
                canceling={cancelingHistoryId === row.history_id}
                onExpand={() => detail.load(row)}
                onCancel={() => handleCancel(row)}
                onHide={() => handleHide(row)}
              />
            );
          })}
        </div>
      )}

      {visibleRows.length > 0 && (
        <div className="sp_bh_page_info">{query.pageNumber.toLocaleString()} 페이지</div>
      )}
      {(query.canGoBack || query.canGoNext) && CursorPaginationComponent && (
        <CursorPaginationComponent
          currentPage={query.pageNumber}
          canGoBack={query.canGoBack}
          canGoNext={query.canGoNext}
          loading={query.loading}
          onPageChange={query.goToPage}
          onBack={query.goBack}
          onNext={query.goNext}
        />
      )}
    </div>
  );
};

export default UnifiedSportsbookHistory;
